import React from 'react';
import { useLanguage } from '../../../context/LanguageContext';

// HuPath v2 design tokens
const HT = {
  yellow: '#FFD166', yellowSoft: '#FFE9A8', yellowBg: '#FFF8E7', yellowShadow: '#E0B554',
  green: '#88C057', greenDark: '#5E9E3E', greenSoft: '#D4E8C0', greenBg: '#E8F3D9',
  coral: '#FF8C42', coralDark: '#E76F51', coralPress: '#B8431E',
  pinkBg: '#FCE4E8',
  brown: '#8B6F47', brownDark: '#6B4F2B',
  text: '#2D2D2D', textSoft: '#6B6B6B', textMuted: '#9A9A9A',
  border: '#E8E0CC', white: '#FFFFFF', teal: '#5AC0B5',
};
const FONT_DISPLAY = '"Fredoka", "Baloo 2", "Nunito", sans-serif';
const FONT_BODY = '"Nunito", "Baloo 2", sans-serif';
const SHADOW_SM = '0 2px 6px rgba(139,111,71,0.08)';
const SHADOW_MD = '0 4px 16px rgba(139,111,71,0.10)';

const SectionTransition = ({ isOpen, sectionTitle, onReview, onNext, isLastSection, isReviewMode = false }) => {
  const { t } = useLanguage();
  if (!isOpen) return null;

  const accent = isReviewMode ? HT.teal : HT.coralDark;
  const accentPress = isReviewMode ? '#3DA89B' : HT.coralPress;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 animate-fadeIn"
      style={{ fontFamily: FONT_BODY, color: HT.text }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(6px)' }}
        onClick={onReview}
      />

      <div
        className="max-w-xl w-full animate-scaleIn"
        style={{
          background: HT.white,
          border: `1px solid ${HT.border}`,
          boxShadow: SHADOW_MD,
          borderRadius: 20,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Top accent band */}
        <div style={{ height: 4, background: accent }} />

        <div className="p-7 md:p-9 flex flex-col items-center text-center">
          {/* Icon badge */}
          <div
            style={{
              width: 84, height: 84, borderRadius: 20,
              background: isReviewMode ? '#E0F5F2' : HT.yellowBg,
              boxShadow: SHADOW_SM,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 18,
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 44, color: accent, fontVariationSettings: "'FILL' 1" }}
            >
              {isReviewMode ? 'menu_book' : 'check_circle'}
            </span>
          </div>

          <h2 style={{
            fontSize: 34, fontWeight: 600, fontFamily: FONT_DISPLAY, color: HT.text,
            letterSpacing: -0.8, marginBottom: 10,
          }}>
            {isReviewMode ? t('reviewFinished') : t('great')}
          </h2>

          <p style={{
            fontSize: 15, fontWeight: 600, color: HT.textSoft,
            lineHeight: 1.5, marginBottom: 22,
          }}>
            {isReviewMode ? (
              <>{t('reviewModeDesc')} <span style={{ color: accent, fontWeight: 800, textDecoration: 'underline', textDecorationColor: `${accent}55` }}>{sectionTitle}</span>.</>
            ) : (
              <>{t('completedSection')} <span style={{ color: HT.greenDark, fontWeight: 800, textDecoration: 'underline', textDecorationColor: `${HT.greenDark}55` }}>{sectionTitle}</span>.</>
            )}
            <br />
            {isReviewMode
              ? (isLastSection ? t('reviewAllFinished') : t('reviewModeDesc'))
              : (isLastSection ? t('submitAndResult') : t('readyForNext'))
            }
          </p>

          {/* Info card */}
          <div
            style={{
              background: isReviewMode ? '#E0F5F2' : !isLastSection ? HT.yellowBg : HT.pinkBg,
              border: `1px solid ${HT.border}`,
              boxShadow: SHADOW_SM,
              borderRadius: 16, padding: 14,
              display: 'flex', gap: 12, textAlign: 'left',
              width: '100%', marginBottom: 22,
            }}
          >
            <div style={{
              width: 40, height: 40, borderRadius: 12, flexShrink: 0,
              background: isReviewMode ? '#C4EDE7' : !isLastSection ? HT.yellowSoft : '#F8C8CE',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: isReviewMode ? HT.teal : !isLastSection ? HT.brownDark : HT.coralDark,
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 22 }}>
                {isReviewMode ? 'info' : !isLastSection ? 'warning' : 'flag'}
              </span>
            </div>
            <div>
              <h4 style={{
                fontSize: 10, fontWeight: 800, letterSpacing: 1.5,
                color: HT.greenDark, marginBottom: 4, textTransform: 'uppercase',
              }}>
                {isReviewMode ? t('reviewMode') : !isLastSection ? t('importantNote') : t('finalStretch')}
              </h4>
              <p style={{ fontSize: 13, fontWeight: 600, color: HT.textSoft, lineHeight: 1.4 }}>
                {isReviewMode
                  ? (isLastSection ? t('reviewAllFinished') : t('reviewModeDesc'))
                  : (!isLastSection ? t('cannotGoBack') : t('completedAllSections'))}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <button
              onClick={onReview}
              style={{
                flex: 1,
                padding: '12px 18px', borderRadius: 999,
                border: `1px solid ${HT.border}`,
                background: HT.white, color: HT.text,
                boxShadow: SHADOW_SM,
                fontFamily: FONT_BODY, fontSize: 14, fontWeight: 800,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span>
              <span>{isReviewMode ? t('back') : t('review')}</span>
            </button>

            <button
              onClick={onNext}
              style={{
                flex: 1.5,
                padding: '14px 20px', borderRadius: 999,
                border: 'none',
                background: accent, color: HT.white,
                boxShadow: `0 4px 0 ${accentPress}`,
                fontFamily: FONT_BODY, fontSize: 15, fontWeight: 800,
                cursor: 'pointer',
                letterSpacing: 0.3,
              }}
            >
              {isReviewMode
                ? (isLastSection ? t('backToResult') : t('nextSection'))
                : (isLastSection ? t('submitAndResult') : t('nextSection'))}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.92) translateY(12px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-scaleIn {
          animation: scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
      `}</style>
    </div>
  );
};

export default SectionTransition;
